import React, { useMemo, useContext } from 'react';
import Text from '../../../../../atomicDesign/atoms/Text';
import Stack from '../../../../../atomicDesign/atoms/Stack';
import Chip from '../../../../../atomicDesign/atoms/Chips/Chip';
import Tooltip from '../../../../../atomicDesign/atoms/Tooltip';
import Box from '../../../../../atomicDesign/atoms/Box';
import type { MonthCell, MonthDeviation } from './types';
import { monthKey, monthName, statusChipColors } from './utils';
import AdminContext from '../../../../../context/admin';

type BaselineMonth = {
  year: number;
  month: number;
};

type Props = {
  monthCoverage: MonthCell[];
  yearsInCoverage: number[];
  excluded: Set<string>;
  onToggleExcluded: (k: string) => void;
  Loading?: boolean;
  monthStatusByKey?: Record<string, MonthDeviation | undefined>;
  baselineStart?: BaselineMonth;
  baselineEnd?: BaselineMonth;
  disabled?: boolean;
};

type AdminContextType = {
  language?: {
    data: {
      noDataAvailable?: string;
    };
    settings?: {
      baseline?: {
        monthsCovered?: string;
        clickMonthsToExclude?: string;
        excluded?: string;
        overBaseline?: string;
        underBaseline?: string;
        included?: string;
        lockTooltip?: {
          locked?: string;
          unlocked?: string;
        };
      };
    };
  };
};

export default function PreviewHeader({
  monthCoverage,
  yearsInCoverage,
  excluded,
  onToggleExcluded,
  Loading = false,
  monthStatusByKey = {},
  baselineStart,
  baselineEnd,
  disabled = false,
}: Props) {
  const { language } = useContext(AdminContext) as AdminContextType;

  const monthValue = (year: number, month: number) => year * 100 + month;

  const isWithinBaselineRange = (year: number, month: number) => {
    if (!baselineStart || !baselineEnd) return true;

    const current = monthValue(year, month);
    const start = monthValue(baselineStart.year, baselineStart.month);
    const end = monthValue(baselineEnd.year, baselineEnd.month);

    return current >= start && current <= end;
  };

  const monthCoverageByKey = useMemo(() => {
    const map = new Map<string, MonthCell>();
    monthCoverage.forEach((c) => {
      map.set(monthKey(c.year, c.month), c);
    });
    return map;
  }, [monthCoverage]);

  const visibleYearsInCoverage = useMemo(() => {
    if (!baselineStart || !baselineEnd) return yearsInCoverage;

    return yearsInCoverage.filter(
      (year) => year >= baselineStart.year && year <= baselineEnd.year
    );
  }, [yearsInCoverage, baselineStart, baselineEnd]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1 }}
        flexWrap="wrap"
      >
        <Text variant="h6">
          {language?.settings?.baseline?.monthsCovered || 'Months covered'}
        </Text>
      </Stack>

      <Stack spacing={1} sx={{ mb: 2 }}>
        <Text variant="body2">
          {language?.settings?.baseline?.clickMonthsToExclude ||
            'Click months to exclude them from the baseline. Orange months has 20% or more deviation from the baseline.'}
        </Text>
      </Stack>

      <Stack spacing={1.5}>
        {visibleYearsInCoverage.map((year) => (
          <Box
            key={year}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
            }}
          >
            <Text
              variant="body2"
              sx={{
                width: { xs: 'auto', sm: 64 },
                minWidth: { xs: 'auto', sm: 64 },
                pt: { xs: 0, sm: '6px' },
                flexShrink: 0,
              }}
            >
              {year}
            </Text>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                width: '100%',
              }}
            >
              {Array.from({ length: 12 }, (_, index) => {
                const month = index + 1;
                const key = monthKey(year, month);
                const insideBaseline = isWithinBaselineRange(year, month);
                const cell = monthCoverageByKey.get(key);

                if (!insideBaseline) {
                  return (
                    <Box
                      key={key}
                      sx={{
                        flex: {
                          xs: '0 0 calc(25% - 6px)',
                          sm: '0 0 calc(16.66% - 6px)',
                          md: '0 0 calc(8.33% - 6px)',
                        },
                        minWidth: 0,
                        height: 34,
                        visibility: 'hidden',
                      }}
                    />
                  );
                }

                if (!cell) {
                  return (
                    <Box
                      key={key}
                      sx={{
                        flex: {
                          xs: '0 0 calc(25% - 6px)',
                          sm: '0 0 calc(16.66% - 6px)',
                          md: '0 0 calc(8.33% - 6px)',
                        },
                        minWidth: 0,
                        height: 34,
                      }}
                    />
                  );
                }

                const isMissing = cell.status === 'missing';
                const isExcluded = excluded.has(key);
                const info = monthStatusByKey[key];
                const pct = info?.deviationPct;

                const tooltipText = disabled
                  ? (language?.settings?.baseline?.lockTooltip?.locked ??
                    'Locked — click to allow automatic edits')
                  : isMissing
                    ? (language?.data?.noDataAvailable ?? 'No data')
                    : isExcluded
                      ? (language?.settings?.baseline?.excluded ??
                        'Excluded from baseline')
                      : typeof pct === 'number'
                        ? `${Math.abs(pct).toFixed(0)}% ${
                            pct >= 0
                              ? (language?.settings?.baseline?.overBaseline ??
                                'over baseline')
                              : (language?.settings?.baseline?.underBaseline ??
                                'under baseline')
                          }`
                        : (language?.settings?.baseline?.included ??
                          'Included in baseline');

                const clickable = !Loading && !isMissing && !disabled;

                return (
                  <Box
                    key={key}
                    sx={{
                      flex: {
                        xs: '0 0 calc(25% - 6px)',
                        sm: '0 0 calc(16.66% - 6px)',
                        md: '0 0 calc(8.33% - 6px)',
                      },
                      minWidth: 0,
                    }}
                  >
                    <Tooltip title={tooltipText}>
                      <span style={{ display: 'block', width: '100%' }}>
                        <Chip
                          label={monthName(cell.month)}
                          variant="outlined"
                          onClick={() => {
                            if (!clickable) return;
                            onToggleExcluded(key);
                          }}
                          sx={{
                            width: '100%',
                            cursor: clickable ? 'pointer' : 'default',
                            opacity: isExcluded ? 0.35 : disabled ? 0.7 : 1,
                            filter: disabled ? 'grayscale(0.6)' : 'none',
                            textDecoration: isExcluded
                              ? 'line-through'
                              : 'none',
                            color: statusChipColors(cell.status).textColor,
                            backgroundColor: statusChipColors(cell.status)
                              .bgColor,
                            m: 0,
                            '& .MuiChip-label': {
                              px: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
